import { z } from 'zod';

/**
 * Lead status enum — backend integer values.
 *
 * Mirrors the legacy `LeadStatus` enum in
 * `src/slices/leads/lead.types.ts`. Exported as a const map (not a TS enum) so
 * downstream code can `LeadStatus.New` without a class instance and the values
 * can be inlined by the bundler.
 */
export const LeadStatus = {
  New: 0,
  Open: 1,
  Approved: 2,
  Converted: 3,
  Cancelled: 4,
  Churned: 5,
} as const;

export type LeadStatusValue = (typeof LeadStatus)[keyof typeof LeadStatus];

export const leadStatusLabels: Record<number, string> = {
  0: 'New',
  1: 'Open',
  2: 'Approved',
  3: 'Converted',
  4: 'Cancelled',
  5: 'Churned',
};

/**
 * Stable list of valid status integers for select dropdowns.
 */
export const leadStatusOptions: LeadStatusValue[] = [
  LeadStatus.New,
  LeadStatus.Open,
  LeadStatus.Approved,
  LeadStatus.Converted,
  LeadStatus.Cancelled,
  LeadStatus.Churned,
];

/**
 * Zod literal-union of the LeadStatus integer values.
 *
 * Using `z.union([z.literal(0), ...])` (rather than `z.number().refine(...)`)
 * keeps `z.input` and `z.output` identical, which avoids the well-known
 * react-hook-form Resolver mismatch (see MIGRATION_PLAYBOOK §3.5).
 */
export const leadStatusSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);

/**
 * Lead form schema — used for both Create and Edit.
 *
 * All "optional" fields stay as `z.string()` (empty allowed) rather than
 * `.optional()` so `z.input == z.output` and the `react-hook-form` Resolver
 * type-checks cleanly (per MIGRATION_PLAYBOOK §3.5). Defaults live in
 * `defaultLeadFormValues` below.
 */
export const leadFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Lead title is required')
    .max(200, 'Lead title must be 200 characters or fewer'),
  contactPerson: z
    .string()
    .trim()
    .min(1, 'Contact person is required')
    .max(100, 'Contact person must be 100 characters or fewer'),
  contactEmail: z
    .string()
    .trim()
    .min(1, 'Contact email is required')
    .email('Enter a valid email'),
  description: z
    .string()
    .trim()
    .min(1, 'Description is required')
    .max(2000, 'Description must be 2000 characters or fewer'),
  phoneNumber: z
    .string()
    .max(50, 'Phone number must be 50 characters or fewer'),
  siteAddress: z
    .string()
    .max(500, 'Site address must be 500 characters or fewer'),
  tenantLocationId: z.string(),
  tentativeProjectStartDate: z.string(),
  tentativeWorkDays: z.number().min(0, 'Work days must be 0 or more'),
  siteTypeId: z.string(),
  notes: z.string().max(2000, 'Notes must be 2000 characters or fewer'),
  /** Only surfaced in the Edit form; in Create it stays at default. */
  leadStatus: leadStatusSchema,
});

export type LeadFormValues = z.infer<typeof leadFormSchema>;

/**
 * Defaults satisfying every required-or-empty field above.
 */
export const defaultLeadFormValues: LeadFormValues = {
  title: '',
  contactPerson: '',
  contactEmail: '',
  description: '',
  phoneNumber: '',
  siteAddress: '',
  tenantLocationId: '',
  tentativeProjectStartDate: '',
  tentativeWorkDays: 0,
  siteTypeId: '',
  notes: '',
  leadStatus: LeadStatus.Open,
};

/**
 * Lead outcome reason values — used by Convert/Churn flows.
 *
 * Kept here for parity with the legacy enum; the modern UI only surfaces these
 * in a future "full conversion form" wave (Wave 4+). Today's confirm-only
 * Convert/Churn modals don't expose this picker.
 */
export const LeadOutcomeReason = {
  Price: 1,
  Budget: 2,
  BetterOffer: 3,
  CompetitorSelected: 4,
  TimelineMismatch: 5,
  Deferred: 6,
  NoResponse: 7,
  NotInterested: 8,
  ApprovedInternally: 9,
  StrategicFit: 10,
  Other: 99,
} as const;

export type LeadOutcomeReasonValue =
  (typeof LeadOutcomeReason)[keyof typeof LeadOutcomeReason];

export const leadOutcomeReasonLabels: Record<number, string> = {
  1: 'Price',
  2: 'Budget',
  3: 'Better Offer',
  4: 'Competitor Selected',
  5: 'Timeline Mismatch',
  6: 'Deferred',
  7: 'No Response',
  8: 'Not Interested',
  9: 'Approved Internally',
  10: 'Strategic Fit',
  99: 'Other',
};
